import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { useSharedServiceHealth } from '../lib/ServiceHealthContext';
import { cn } from '../lib/utils';
import React from 'react';

const PILL_TONE: Record<string, string> = {
  healthy: 'bg-green-500',
  degraded: 'bg-yellow-500',
  down: 'bg-red-500',
};

export function StatusPill() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthStore();
  const { query, payload, health } = useSharedServiceHealth();
  const { isLoading, error } = query;
  const data = payload;

  if (!isAuthenticated) {
    return null;
  }

  // After the first honest snapshot, never replace chrome with Loading on route change.
  if (isLoading && health.total <= 0 && !data) {
    return (
      <div className="status-pill bg-gray-200 text-gray-600">
        <div className="w-2 h-2 bg-gray-400 rounded-full mr-2 animate-pulse" />
        {t('status.loading')}
      </div>
    );
  }

  if (error && !data) {
    return (
      <div
        className="status-pill bg-gray-200 text-gray-600 cursor-help"
        title={error.message}
      >
        <div className="w-2 h-2 bg-gray-400 rounded-full mr-2" />
        {t('status.error')}
      </div>
    );
  }

  if (!data && health.total <= 0) {
    return null;
  }

  const statusText = health.status === 'Healthy'
    ? t('status.healthy')
    : health.status === 'Degraded'
      ? t('status.degraded')
      : health.status === 'Critical'
        ? t('status.unhealthy')
        : t('status.error');
  const label = health.total > 0
    ? `${statusText} (${health.ok}/${health.total})`
    : statusText;
  const downNames = data?.components
    ? Object.entries(data.components)
        .filter(([, service]) => String((service as { status?: string }).status || '') !== 'ok' && (service as { ok?: boolean }).ok !== true)
        .map(([name]) => name)
        .slice(0, 3)
    : [];
  const cacheAgeSec = typeof data?.cache?.age_ms === 'number'
    ? Math.round(data.cache.age_ms / 1000)
    : null;
  const staleNote = cacheAgeSec != null
    ? ` · snapshot ${cacheAgeSec}s old`
    : '';
  const tooltipText = (health.down === 0 && health.degraded === 0
    ? health.label
    : `${health.down + health.degraded} ${t('status.subsystems')} ${t('status.failing')}${downNames.length ? `: ${downNames.join(', ')}` : ''}`) + staleNote;

  return (
    <div
      className={cn('status-pill text-white cursor-help', PILL_TONE[health.variant] || 'bg-gray-500')}
      title={tooltipText}
    >
      <div className={cn('w-2 h-2 rounded-full mr-2', PILL_TONE[health.variant] || 'bg-gray-400')} />
      <span className="text-xs font-medium">
        {label}
      </span>
    </div>
  );
}
